import * as XLSX from 'xlsx'

// Must match the backend's own cap (bulkAddCallers in yandle_admin_portal_1)
// -- checked here too so an oversized file is rejected immediately instead
// of only failing after a network round-trip.
export const MAX_BULK_CALLERS = 1000

const NAME_HEADERS = ['caller_name', 'callername', 'caller name', 'name']
const COUNTRY_CODE_HEADERS = ['country_code', 'countrycode', 'country code', 'code']
const CONTACT_NUMBER_HEADERS = [
  'caller_contact_number',
  'contact_number',
  'contact number',
  'phone',
  'phone_number',
  'number',
]

function normalizeHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

function findColumnIndex(headers, candidates) {
  const normalized = headers.map(normalizeHeader)
  for (const candidate of candidates) {
    const needle = normalizeHeader(candidate)
    const idx = normalized.findIndex(
      (header) => header === needle || header.replace(/\s/g, '') === needle.replace(/\s/g, ''),
    )
    if (idx >= 0) return idx
  }
  return -1
}

function cellText(value) {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Avoid scientific notation for phone-like numbers from Excel.
    return String(value)
  }
  return String(value).trim()
}

/**
 * Parse a browser File (.xlsx / .xls / .csv) into
 * [{ caller_name, country_code, caller_contact_number }, ...].
 * Duplicate rows (same country_code + number) within the file are
 * collapsed to the first occurrence -- the backend also dedupes against
 * what already exists in the org, so this only handles within-file repeats.
 */
export async function parseBulkCallersFile(file) {
  if (!file) {
    return { ok: false, error: 'Choose an Excel or CSV file.', callers: [] }
  }

  try {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    if (!sheetName) {
      return { ok: false, error: 'The spreadsheet has no sheets.', callers: [] }
    }

    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: '',
      raw: false,
    })

    if (!Array.isArray(rows) || rows.length < 2) {
      return {
        ok: false,
        error: 'Spreadsheet needs a header row and at least one data row.',
        callers: [],
      }
    }

    const headers = (rows[0] || []).map((cell) => cellText(cell))
    const nameCol = findColumnIndex(headers, NAME_HEADERS)
    const codeCol = findColumnIndex(headers, COUNTRY_CODE_HEADERS)
    const numberCol = findColumnIndex(headers, CONTACT_NUMBER_HEADERS)

    if (codeCol < 0 || numberCol < 0) {
      return {
        ok: false,
        error: 'Columns required: country_code and caller_contact_number (caller_name optional).',
        callers: [],
      }
    }

    const callers = []
    const seen = new Set()

    for (let i = 1; i < rows.length; i += 1) {
      const row = rows[i]
      if (!Array.isArray(row)) continue
      const caller_name = nameCol >= 0 ? cellText(row[nameCol]) : ''
      let country_code = cellText(row[codeCol])
      const caller_contact_number = cellText(row[numberCol])
      if (!country_code && !caller_contact_number) continue
      if (!country_code || !caller_contact_number) continue

      if (!country_code.startsWith('+')) country_code = `+${country_code}`

      const key = `${country_code}${caller_contact_number}`
      if (seen.has(key)) continue
      seen.add(key)

      callers.push({
        caller_name: caller_name || null,
        country_code,
        caller_contact_number,
      })
    }

    if (callers.length > MAX_BULK_CALLERS) {
      return {
        ok: false,
        error: `Maximum ${MAX_BULK_CALLERS} callers per upload -- this file has ${callers.length}. Split it into smaller files.`,
        callers: [],
      }
    }

    if (!callers.length) {
      return {
        ok: false,
        error: 'No valid rows found (need country_code + caller_contact_number).',
        callers: [],
      }
    }

    return { ok: true, callers, error: '' }
  } catch (error) {
    return {
      ok: false,
      error: error?.message || 'Failed to parse spreadsheet.',
      callers: [],
    }
  }
}

/**
 * Builds and downloads a starter .xlsx with the exact headers this parser
 * looks for, plus one example row, so a user knows what to fill in without
 * guessing column names/order.
 */
export function downloadCallersTemplate() {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['caller_name', 'country_code', 'caller_contact_number'],
    ['Jane Doe', '+91', '9876543210'],
  ])
  worksheet['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 22 }]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Callers')
  XLSX.writeFile(workbook, 'callers_bulk_upload_template.xlsx')
}
